import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { buildStatusMapping } from "../../LeadStatus";
import { buildCountryCodeMapping } from "../../utils/countryCodes";
import {
  LeadPageTranslatedContent,
  ReturnedTranslatedContentLeadPageHook,
} from "../types";

export const useLeadsTranslatedContent =
  (): ReturnedTranslatedContentLeadPageHook => {
    const { t, i18n } = useTranslation();

    const translatedContent: LeadPageTranslatedContent = useMemo(
      () => ({
        pageHeader: t("leadsPage_header"),
        noDataFoundTitle: t("leadsPage_no_data_found_title"),
        noDataFoundText: t("leadsPage_no_data_found_text"),
        noResultsFoundText: t("leadsPage_table_no_results_found_text"),
        noDataFoundCtaText: t("leadsPage_table_go_to_overview_link"),
        exportCSVText: t("leadsPage_export_csv_button"),
        exportCSVToastErrorTitle: t("leadsPage_export_csv_toast_error_title"),
        exportCSVToastErrorDescription: t(
          "leadsPage_export_csv_toast_error_description",
        ),
        paginationLabelForShowing: t("leadsPage_pagination_label_showing"),
        paginationLabelForOf: t("leadsPage_pagination_label_of"),
        today: t("leadsPage_datepicker_shortcuts_today"),
        yesterday: t("leadsPage_datepicker_shortcuts_yesterday"),
        past: (period: number) =>
          t("leadsPage_datepicker_shortcuts_lastperiod", { period: period }),
        currentMonth: t("leadsPage_datepicker_shortcuts_thismonth"),
        pastMonth: t("leadsPage_datepicker_shortcuts_lastmonth"),
        cancel: t("leadsPage_datepicker_footer_cancel"),
        apply: t("leadsPage_datepicker_shortcuts_apply"),
        columnLabels: {
          nameColumnLabel: t("leadsPage_table_column_label_name"),
          emailColumnLabel: t("leadsPage_table_column_label_email"),
          clientIdColumnLabel: t("leadsPage_table_column_label_client_id"),
          countryColumnLabel: t("leadsPage_table_column_label_country"),
          createdColumnLabel: t("leadsPage_table_column_label_created"),
          signupDateColumnLabel: t("leadsPage_table_column_label_signup_date"),
          statusColumnLabel: t("leadsPage_table_column_label_status"),
          onboardingStatusColumnLabel: t(
            "leadsPage_table_column_label_onboarding_status",
          ),
        },
        searchLabel: t("leadsPage_search_label"),
        datePickerLabel: t("leadsPage_datepicker_label"),
        signupDatePickerLabel: t("leadsPage_table_column_label_signup_date"),
        filterPlaceholder: t("leadsPage_input_placeholder_v2_search"),
        filterNoPIIPlaceholder: t(
          "leadsPage_input_placeholder_v2_search_no_pii",
        ),
        filterPIINamePlaceholder: t(
          "leadsPage_input_placeholder_v2_search_pii_name",
        ),
        filterPIIEmailPlaceholder: t(
          "leadsPage_input_placeholder_v2_search_pii_email",
        ),
        datePickerPlaceholder: t("leadsPage_datepicker_placeholder"),
        statusesFilterLabel: t("leadsPage_statuses_filter_label"),
        countryFilterLabel: t("leadsPage_country_filter_label", {
          defaultValue: "Country",
        }),
        dropDownLabels: {
          all: t("leadsPage_dropdown_selectAll"),
          none: t("leadsPage_dropdown_none"),
          selectStatusLabel: t("leadsPage_dropdown_selectStatus"),
          allItemsSelectedLabel: t("leadsPage_dropdown_allItemsSelected"),
          itemsSelectedLabel: t("leadsPage_dropdown_itemsSelected"),
          clearSelectionLabel: t("leadsPage_dropdown_clearItems"),
          selectAllLabel: t("leadsPage_dropdown_selectAll"),
          applyButtonLabel: t("leadsPage_dropdown_apply"),
        },
        countryDropDownLabels: {
          all: t("leadsPage_dropdown_selectAll"),
          none: t("leadsPage_dropdown_none"),
          selectStatusLabel: t("leadsPage_dropdown_selectStatus"),
          allItemsSelectedLabel: t(
            "leadsPage_dropdown_country_allItemsSelected",
          ),
          itemsSelectedLabel: t("leadsPage_dropdown_country_itemsSelected"),
          clearSelectionLabel: t("leadsPage_dropdown_clearItems"),
          selectAllLabel: t("leadsPage_dropdown_selectAll"),
          applyButtonLabel: t("leadsPage_dropdown_apply"),
        },
        headlineMetrics: {
          profileCreated: t("leadsPage_headline_metrics_profile_created"),
          approved: t("leadsPage_headline_metrics_approved_applications"),
          onboarding: t("leadsPage_headline_metrics_onboarding"),
          onboardingTooltip: t("leadsPage_headline_metrics_onboarding_tooltip"),
          headlineMetricsChange: t("leadsPage_headline_metrics_change"),
        },
      }),
      [t],
    );

    const leadStatusMapping = useMemo(() => buildStatusMapping(t), [t]);
    const countryCodeMapping = useMemo(() => buildCountryCodeMapping(t), [t]);
    return {
      translatedContent,
      language: i18n.language,
      leadStatusMapping,
      countryCodeMapping,
    };
  };
